import React, {useMemo, useState} from "react";
import {Alert, Button, Checkbox, Input, Modal} from "antd";
import {formatAiPrompt} from "./AiPromptFormatting";
import * as styles from "./AiPromptEditor.module.css";

interface Props {
    id?: string;
    value?: string;
    onChange?: (value: string) => void;
    disabled?: boolean;
}

function AiPromptEditor({id, value = "", onChange, disabled}: Props) {
    const [originalText, setOriginalText] = useState<string>();
    const [joinWrappedLines, setJoinWrappedLines] = useState(false);
    const formattedText = useMemo(() => formatAiPrompt(originalText ?? "", joinWrappedLines), [originalText, joinWrappedLines]);
    const hasChanges = originalText !== formattedText;

    return <>
        <Input.TextArea
            id={id}
            value={value}
            onChange={event => onChange?.(event.target.value)}
            disabled={disabled}
            autoSize={{minRows: 12, maxRows: 30}}
            maxLength={10000}
            showCount
        />
        <div className={styles.actions}>
            <Button disabled={disabled || value.trim() === ""} onClick={() => {
                setJoinWrappedLines(false);
                setOriginalText(value);
            }}>Format text</Button>
        </div>
        <Modal
            title="Format prompt text"
            open={originalText !== undefined}
            width={1100}
            okText="Use formatted text"
            cancelText="Cancel"
            okButtonProps={{disabled: !hasChanges || disabled}}
            onCancel={() => setOriginalText(undefined)}
            onOk={() => {
                onChange?.(formattedText);

                setOriginalText(undefined);
            }}
        >
            <p>Remove shared indentation and blank lines at the beginning and end. Review the preview before applying.</p>
            <Checkbox checked={joinWrappedLines} onChange={event => setJoinWrappedLines(event.target.checked)}>
                Join wrapped lines within paragraphs
            </Checkbox>
            <p className={styles.hint}>Lists, code blocks and explicit line breaks are kept separate. Changes will be saved when you save the prompt.</p>
            {!hasChanges && <Alert type="info" showIcon message="No formatting changes are needed."/>}
            <div className={styles.preview}>
                <section>
                    <h3>Current text</h3>
                    <pre aria-label="Current prompt text">{originalText}</pre>
                </section>
                <section>
                    <h3>Formatted text</h3>
                    <pre aria-label="Formatted prompt text">{formattedText}</pre>
                </section>
            </div>
        </Modal>
    </>;
}

export default AiPromptEditor;
